import type { InfoRichTextEditorDialog } from "../../../dialogs/info/rich-text-editor";

import sanitizeHtml from "sanitize-html";
import { twMerge } from "tailwind-merge";

import { addExternalLinkAriaLabels } from "../../../helpers/info/rich-text-editor";

const ANCHOR_TAG_CSS =
  "[&_a]:focus-outline [&_a[type='external']]:after:ml-1 [&_a[type='external']]:after:inline-block [&_a[type='external']]:after:content-['↗'] [&_a]:cursor-pointer [&_a]:text-royal-blue [&_a]:hover:underline group-data-[has-dark-theme=true]/theme:[&_a]:text-white group-data-[has-dark-theme=true]/theme:[&_a]:underline group-data-[has-dark-theme=true]/theme:[&_a]:outline-white";
const BASE_CSS =
  "mt-4 text-body text-charcoal [&_li]:text-left group-data-[has-dark-theme=true]/theme:text-white group-data-[has-dark-theme=true]/theme:outline-white md:mt-6";
const BLOCKQUOTE_CSS =
  "relative [&_blockquote:first-of-type::before]:absolute [&_blockquote:first-of-type::before]:left-0 [&_blockquote:first-of-type::before]:top-0 [&_blockquote:first-of-type::before]:leading-10 [&_blockquote:first-of-type::before]:content-['“'] [&_blockquote]:text-body [&_blockquote]:text-core-blue [&_blockquote]:before:text-[4rem] [&_blockquote]:before:text-sky-blue group-data-[has-dark-theme=true]/theme:[&_blockquote]:text-white group-data-[has-dark-theme=true]/theme:[&_blockquote]:before:text-white [&_blockquote]:md:text-m-heading [&_blockquote_p]:ml-8 [&_blockquote_p]:font-normal md:[&_blockquote_p]:!leading-7";
const CITATION_CSS =
  "[&_blockquote_p_cite]:ml-0 [&_cite]:ml-8 [&_cite]:mt-2 [&_cite]:block [&_cite]:!text-s-body [&_cite]:!font-semibold [&_cite]:uppercase [&_cite]:not-italic [&_cite]:text-slate-gray group-data-[has-dark-theme=true]/theme:[&_cite]:text-white md:[&_cite]:mt-3";
const H3_CSS =
  "[&_*+h3]:mt-4 md:[&_*+h3]:mt-6 [&_h3]:mb-3 [&_h3]:!text-m-heading [&_h3]:text-core-blue group-data-[has-dark-theme=true]/theme:[&_h3]:text-white";
const H4_CSS =
  "[&_*+h4]:mt-3 md:[&_*+h4]:mt-5 [&_h4]:mb-2 [&_h4]:!text-s-heading [&_h4]:text-core-blue group-data-[has-dark-theme=true]/theme:[&_h4]:text-white";
const LEGAL_TEXT_CSS =
  "[&_ol>li:has(small)]:marker:text-legal [&_small]:text-legal";
const ORDERED_LIST_CSS =
  "[&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-10 [&_ol_li]:mb-1 [&_ol_ol]:my-1";
const PARAGRAPH_CSS = "[&>p]:mt-2 md:[&>p]:mt-3";
const UNORDERED_LIST_CSS =
  "[&_li]:mb-1 [&_ul>li]:marker:text-bright-blue group-data-[has-dark-theme=true]/theme:[&_ul>li]:marker:text-white [&_ul]:my-2 [&_ul]:list-[square] [&_ul]:pl-10 [&_ul_ul>li]:marker:text-electric-blue [&_ul_ul]:my-1 [&_ul_ul_ul>li]:marker:text-royal-blue";

export const InfoRichTextEditor = ({
  className,
  content,
}: InfoRichTextEditorDialog & { className?: string | undefined }) => {
  const addedAriaLabelsContent = addExternalLinkAriaLabels({
    html: content,
  });

  const sanitizedContent = addedAriaLabelsContent
    ? sanitizeHtml(addedAriaLabelsContent, {
        allowedAttributes: {
          "*": ["class"],
          a: ["aria-label", "href", "target", "type"],
        },
      })
    : "";

  if (!sanitizedContent) {
    return null;
  }

  const twClassName = twMerge(
    BASE_CSS,
    ANCHOR_TAG_CSS,
    BLOCKQUOTE_CSS,
    CITATION_CSS,
    H3_CSS,
    H4_CSS,
    LEGAL_TEXT_CSS,
    ORDERED_LIST_CSS,
    PARAGRAPH_CSS,
    UNORDERED_LIST_CSS,
    className,
  );

  return (
    <div className="container mx-auto">
      <div
        className={twClassName}
        dangerouslySetInnerHTML={{ __html: sanitizedContent }}
      />
    </div>
  );
};
